import { useEffect } from 'react';
import { useSettingsStore } from '../stores/useSettingsStore';
import { logThemePersistenceStatus } from '../utils/themeUtils';

/**
 * Custom hook to apply theme classes to the document root based on user settings.
 * Ensures dark mode is applied by default, even for system preference unless explicitly light.
 * Also handles cross-tab synchronization and storage events.
 */
export const useThemeEffect = () => {
  const { settings } = useSettingsStore();
  const { theme } = settings;

  // Log persistence status in development mode
  useEffect(() => {
    if (import.meta.env.DEV) {
      logThemePersistenceStatus();
    }
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    
    // Remove any existing theme classes
    root.classList.remove('dark', 'light');
    
    switch (theme) {
      case 'dark':
        root.classList.add('dark');
        break;
        
      case 'light':
        root.classList.add('light');
        break;
        
      case 'system':
        // Check system preference, but default to dark if no preference or if dark
        const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
        if (mediaQuery.matches) {
          // Only use light mode if system explicitly prefers light
          root.classList.add('light');
        } else {
          // Default to dark for system preference (dark or no-preference)
          root.classList.add('dark');
        }
        break;
        
      default:
        // Fallback to dark mode
        root.classList.add('dark');
    }
    
    // Set up system theme listener for when theme is 'system'
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        const root = document.documentElement;
        root.classList.remove('dark', 'light');
        
        if (e.matches) {
          // System switched to light mode
          root.classList.add('light');
        } else {
          // System switched to dark mode or no preference
          root.classList.add('dark');
        }
      }
    };
    
    // Only add listener if theme is 'system'
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      
      // Cleanup listener
      return () => {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      };
    }
  }, [theme]);

  // Set up cross-tab synchronization for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Only respond to changes in our settings storage
      if (e.key === 'settings-storage' && e.newValue) {
        try {
          // Parse the new settings
          const newSettings = JSON.parse(e.newValue);
          const newTheme = newSettings?.state?.settings?.theme;
          
          // If theme changed in another tab, Zustand will automatically sync
          // But we want to ensure the DOM classes are updated immediately
          if (newTheme && newTheme !== theme) {
            const root = document.documentElement;
            root.classList.remove('dark', 'light');
            
            if (newTheme === 'dark' || (newTheme === 'system' && !window.matchMedia('(prefers-color-scheme: light)').matches)) {
              root.classList.add('dark');
            } else {
              root.classList.add('light');
            }
          }
        } catch (error) {
          console.warn('Failed to parse localStorage settings:', error);
        }
      }
    };

    // Listen for localStorage changes from other tabs
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [theme]);
  
  // Return the current effective theme for components that might need it
  const getEffectiveTheme = (): 'dark' | 'light' => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return theme;
  };
  
  return {
    effectiveTheme: getEffectiveTheme(),
    isDark: getEffectiveTheme() === 'dark',
    isLight: getEffectiveTheme() === 'light'
  };
}; 